'use client';
import React, { useState } from 'react';

interface CleanOption {
  id: string;
  label: string;
  description: string;
  fn: (text: string) => string;
}

const CLEAN_OPTIONS: CleanOption[] = [
  { id: 'extra-spaces', label: 'Remove extra spaces', description: 'Collapse multiple spaces into one', fn: (t) => t.replace(/[ \t]+/g, ' ').trim() },
  { id: 'empty-lines', label: 'Remove empty lines', description: 'Delete blank lines between paragraphs', fn: (t) => t.replace(/\n\s*\n+/g, '\n').trim() },
  { id: 'line-breaks', label: 'Remove all line breaks', description: 'Join all lines into one paragraph', fn: (t) => t.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim() },
  { id: 'special-chars', label: 'Remove special characters', description: 'Keep only letters, numbers, spaces', fn: (t) => t.replace(/[^a-zA-Z0-9\s.,!?'"()-]/g, '').trim() },
  { id: 'html-tags', label: 'Strip HTML tags', description: 'Remove all <tag> elements', fn: (t) => t.replace(/<[^>]*>/g, '').trim() },
  { id: 'trim-lines', label: 'Trim each line', description: 'Remove leading/trailing spaces per line', fn: (t) => t.split('\n').map((l) => l.trim()).join('\n').trim() },
  { id: 'duplicate-lines', label: 'Remove duplicate lines', description: 'Keep only unique lines', fn: (t) => [...new Set(t.split('\n'))].join('\n').trim() },
  { id: 'numbers', label: 'Remove numbers', description: 'Delete all numeric digits', fn: (t) => t.replace(/\d/g, '').trim() },
];

export default function TextCleanerTool() {
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(['extra-spaces', 'empty-lines', 'trim-lines']));
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  const toggleOption = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleClean = () => {
    let cleaned = text;
    CLEAN_OPTIONS.filter((o) => selected.has(o.id)).forEach((o) => { cleaned = o.fn(cleaned); });
    setResult(cleaned);
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="font-semibold text-text-primary text-sm mb-2 block">Input Text</label>
        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setResult(''); }}
          placeholder="Paste your text here to clean it…"
          className="w-full h-36 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-text-primary resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
        />
      </div>

      <div>
        <label className="font-semibold text-text-primary text-sm mb-3 block">Cleaning Options</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CLEAN_OPTIONS.map((opt) => (
            <label key={opt.id} className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors ${selected.has(opt.id) ? 'bg-blue-50 border border-blue-100' : 'bg-surface border border-transparent hover:bg-gray-100'}`}>
              <input
                type="checkbox"
                checked={selected.has(opt.id)}
                onChange={() => toggleOption(opt.id)}
                className="mt-0.5 rounded"
                style={{ accentColor: '#2563EB' }}
              />
              <div>
                <p className="text-sm font-medium text-text-primary">{opt.label}</p>
                <p className="text-xs text-text-muted">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <button
        onClick={handleClean}
        disabled={!text || selected.size === 0}
        className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Clean Text
      </button>

      {result && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-semibold text-text-primary text-sm">Cleaned Text</label>
            <button onClick={handleCopy} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <textarea
            readOnly
            value={result}
            className="w-full h-36 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-text-secondary resize-none focus:outline-none bg-gray-50"
          />
          <p className="text-xs text-text-muted mt-1">
            {text.length} → {result.length} characters ({text.length - result.length > 0 ? `-${text.length - result.length}` : `+${result.length - text.length}`} chars)
          </p>
        </div>
      )}
    </div>
  );
}
